import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/field';
import { Eye, EyeOff } from 'lucide-react';
import { authLogin, authGoogleUrl } from '@/lib/api';
import { toast } from 'sonner';

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    const error = sessionStorage.getItem('auth_error');
    if (error) {
      sessionStorage.removeItem('auth_error');
      toast.error(error);
    }
  }, []);

  if (user) {
    navigate('/dashboard', { replace: true });
    return null;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Please enter your email and password'); return; }
    setLoading(true);
    try {
      const res = await authLogin(email.trim(), password);
      login(res.data);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      const res = await authGoogleUrl('login');
      localStorage.setItem('oauth_mode', 'login');
      window.location.href = res.data.url;
    } catch {
      toast.error('Failed to connect to Google. Please try again.');
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-[400px]">
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/logo192.png" alt="Flowlancer" className="w-8 h-8 rounded-md" />
          <span className="font-display text-subhead text-ink">FlowLancer</span>
        </Link>

        <h1 className="mt-10 font-display text-display-l text-ink">Welcome back</h1>
        <p className="mt-3 text-body text-ink-muted">Sign in to your FlowLancer account.</p>

        <form onSubmit={handleLogin} className="mt-10 space-y-5">
          <Field label="Email address" htmlFor="email">
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </Field>

          <Field label="Password" htmlFor="password">
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-subtle hover:text-ink"
              >
                {showPassword
                  ? <EyeOff className="w-4 h-4" strokeWidth={1.5} />
                  : <Eye className="w-4 h-4" strokeWidth={1.5} />}
              </button>
            </div>
          </Field>

          <div className="flex justify-end">
            <Link to="/forgot-password" className="text-small text-forest hover:text-forest-hover">
              Forgot password?
            </Link>
          </div>

          <Button type="submit" disabled={loading} className="w-full h-11">
            {loading ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>

        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-line" />
          <span className="text-micro uppercase text-ink-subtle">or</span>
          <div className="flex-1 h-px bg-line" />
        </div>

        <Button
          onClick={handleGoogleLogin}
          disabled={googleLoading}
          variant="outline"
          className="w-full h-11"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
          </svg>
          {googleLoading ? 'Redirecting…' : 'Login with Google'}
        </Button>

        <p className="mt-8 text-small text-ink-muted">
          New user?{' '}
          <Link to="/signup" className="text-forest hover:text-forest-hover">
            Create account
          </Link>
        </p>

        <button
          onClick={() => navigate('/')}
          className="mt-6 text-small text-ink-subtle hover:text-ink transition-colors"
        >
          Back to home
        </button>
      </div>
    </div>
  );
}
